<template>
  <BaseModal
    :is-open="isOpen"
    title="계좌비밀번호"
    :show-default-footer="false"
    :close-on-overlay-click="false"
    :close-on-escape="true"
    content-padding="none"
    @close="handleClose"
    @update:is-open="$emit('update:isOpen', $event)"
  >
    <AccountPasswordResetInputContent v-if="step === 1" v-model:pin="password" />
    <AccountPasswordResetReInputContent v-if="step === 2" v-model:pin="passwordConfirm" />
    <!-- 푸터 영역 -->
    <template #footer>
      <div class="flex items-center justify-center gap-3">
        <BaseButton
          variant="contained"
          color="primary"
          label="확인"
          :disabled="isConfirmDisabled"
          @click="handleAgree"
          fullWidth
        />
        <BaseButton variant="outlined" color="white" label="취소" @click="handleCancel" fullWidth />
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import AccountPasswordResetReInputContent from '../content/accountPasswordReset/AccountPasswordResetReInputContent.vue';
import AccountPasswordResetInputContent from '../content/accountPasswordReset/AccountPasswordResetInputContent.vue';
import { useGlobalDialogManager } from '@/composables/useGlobalDialogManager';
import { BaseModal, BaseButton } from '@template/ui';
import { sha256 } from '@template/utils';
import { computed, ref } from 'vue';

const isOpen = defineModel<boolean>('isOpen', { required: true });
const globalDialogManager = useGlobalDialogManager();
const step = ref<number>(1);
const password = ref<string[]>([]);
const passwordConfirm = ref<string[]>([]);

interface Emits {
  (e: 'close'): void;
  (e: 'confirm', password: string): void;
  (e: 'update:isOpen', value: boolean): void;
}

const emit = defineEmits<Emits>();

const isConfirmDisabled = computed(() => {
  const currentPassword = step.value === 1 ? password.value : passwordConfirm.value;
  return currentPassword.length !== 6;
});

const handleCancel = () => {
  if (step.value === 2) {
    // Step 2일 때: 이전 단계로 돌아가기
    step.value = 1;
    password.value = [];
    passwordConfirm.value = [];
  } else {
    // Step 1일 때: 다이얼로그 닫기
    handleClose();
  }
};

const handleClose = () => {
  password.value = [];
  passwordConfirm.value = [];
  step.value = 1;
  emit('close');
  emit('update:isOpen', false);
};

const handleAgree = async () => {
  if (step.value === 1) {
    if (password.value.length !== 6) {
      return;
    }

    step.value = 2;
    passwordConfirm.value = []; // 다음 단계를 위해 비밀번호 초기화
  } else if (step.value === 2) {
    if (passwordConfirm.value.length !== 6) {
      return;
    }

    // getResetPasswordInfo를 통해 비밀번호 일치 확인 후 결과 반환
    try {
      const passwordHash = await sha256(password.value.join(''));
      const passwordConfirmHash = await sha256(passwordConfirm.value.join(''));

      if (passwordHash === passwordConfirmHash) {
        emit('confirm', passwordHash);
        handleClose();
      } else {
        throw new Error('비밀번호가 일치하지 않습니다.');
      }
    } catch (error) {
      // 비밀번호가 일치하지 않을 경우 에러 처리
      console.error('비밀번호 재설정 오류:', error);
      globalDialogManager.open('accountPasswordResetMismatch');
      // 재입력 화면 초기화
      step.value = 1;
      password.value = [];
      passwordConfirm.value = [];
      // 필요시 에러 메시지를 사용자에게 표시
    }
  }
};
</script>
